import { useCallback, useEffect, useRef, useState } from 'react';

const COMPLETED_CARD_HIDE_DELAY_MS = 5000;

type StickyProgressOperation = {
    startedAt: number;
};

export const useStickyProgressCard = <T extends StickyProgressOperation>(progress: T | null) => {
    const [snapshot, setSnapshot] = useState<T | null>(null);
    const [dismissedStartedAt, setDismissedStartedAt] = useState<number | null>(null);
    const [isGracePeriod, setIsGracePeriod] = useState(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }

        if (progress) {
            setSnapshot(progress);
            setIsGracePeriod(false);
            setDismissedStartedAt((current) => {
                if (current === null || current === progress.startedAt) {
                    return current;
                }

                return null;
            });
            return;
        }

        if (!snapshot) {
            setIsGracePeriod(false);
            return;
        }

        setIsGracePeriod(true);
        hideTimerRef.current = setTimeout(() => {
            setSnapshot(null);
            setIsGracePeriod(false);
        }, COMPLETED_CARD_HIDE_DELAY_MS);

        return () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, [progress, snapshot]);

    useEffect(() => {
        return () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, []);

    const dismiss = useCallback(() => {
        if (!snapshot) {
            return;
        }

        setDismissedStartedAt(snapshot.startedAt);

        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }

        if (!progress) {
            setSnapshot(null);
            setIsGracePeriod(false);
        }
    }, [progress, snapshot]);

    return {
        snapshot,
        isGracePeriod,
        isVisible: !!snapshot && dismissedStartedAt !== snapshot.startedAt,
        dismiss,
    };
};
